import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { pick } from 'ramda';
import { usePushSubscription } from 'server';

import { useUserSelector } from '@core/js/store/hooks/useUserSelector';

import { useDynamicTags } from '@interlayer/js/app/hooks/useDynamicTags';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';
import { setIsShowOccupationPopup } from '@interlayer/js/hooks/user/store/buzz/actions';

export const useCheckOccupationTags = ({ shouldCheckRequested, shouldCheckForce } = {}) => {
    const [
        dTagOccupationRequestedPlayerTag = 'Athena/Occupation=Requested',
        dTagOccupationForcePlayerTag = 'Athena/Occupation=Force'
    ] = useDynamicTags([
        DYNAMIC_TAG_NAMES.occupationRequestedPlayerTag,
        DYNAMIC_TAG_NAMES.occupationForcePlayerTag
    ]);

    const occupationTags = useMemo(() => {
        return [
            shouldCheckRequested
            && dTagOccupationRequestedPlayerTag.trim().toLowerCase() !== 'false'
            && dTagOccupationRequestedPlayerTag.split('='),
            shouldCheckForce
            && dTagOccupationForcePlayerTag.trim().toLowerCase() !== 'false'
            && dTagOccupationForcePlayerTag.split('=')
        ].filter(Boolean);
    }, [dTagOccupationForcePlayerTag, dTagOccupationRequestedPlayerTag, shouldCheckForce, shouldCheckRequested]);

    return useCallback((playerTags) => {
        return occupationTags.length
            && occupationTags.some(([name, value]) => playerTags[name] && playerTags[name].value === value);
    }, [occupationTags]);
};

export const useCheckOccupation = ({ shouldCheckRequested = true, shouldCheckForce = true } = {}) => {
    const dispatch = useDispatch();
    const {
        allPlayerTags, isOccupationSet, isShowOccupationPopup
    } = useUserSelector(pick(['allPlayerTags', 'isOccupationSet', 'isShowOccupationPopup']));

    const checkTags = useCheckOccupationTags({ shouldCheckForce, shouldCheckRequested });

    return useCallback(({ playerTags = allPlayerTags } = {}) => {
        const isShowOccupation = !isOccupationSet
            && !isShowOccupationPopup
            && playerTags
            && checkTags(playerTags);

        if (isShowOccupation) {
            dispatch(setIsShowOccupationPopup(true));
        }

        return isShowOccupation;
    }, [allPlayerTags, checkTags, dispatch, isOccupationSet, isShowOccupationPopup]);
};

export const useTriggerOccupationPopup = ({ shouldCheckRequested, shouldCheckForce } = {}) => {
    const [
        dTagOccupationRequestedPlayerTag = 'Athena/Occupation=Requested'
    ] = useDynamicTags([
        DYNAMIC_TAG_NAMES.occupationRequestedPlayerTag
    ]);

    const dispatch = useDispatch();
    const checkOccupationTags = useCheckOccupationTags({ shouldCheckRequested, shouldCheckForce });

    const subscriptionTag = useCallback(() => (pushMessage) => {
        const tagData = pushMessage.data?.notification?.notificationPlayerTagChangeInfo;

        if (tagData?.tag === dTagOccupationRequestedPlayerTag.trim().split('=')[0]) {
            const isShowPopup = checkOccupationTags({
                [tagData.tag]: {
                    name: tagData.tag,
                    value: tagData.value
                }
            });

            isShowPopup && dispatch(setIsShowOccupationPopup(true));
        }
    }, [checkOccupationTags, dTagOccupationRequestedPlayerTag, dispatch]);

    usePushSubscription(31133, subscriptionTag);
};

